import PropTypes from 'prop-types';
import React from 'react';
import './Breadcrumbs.css';

export class Crumb {
  /**
   * Static counter to generate unique IDs for each Crumb instance.
   * @type {number}
   */
  static #instanceCounter = 0;

  /**
   * Generates a unique ID for each Crumb instance.
   * @returns {number} A unique ID for the Crumb instance.
   */
  static #incrementAndReturnCounter() {
    Crumb.#instanceCounter += 1;

    return Crumb.#instanceCounter;
  }

  /**
   * Factory method to create a separator crumb, which is not clickable and not current.
   * @param {string} [customSeparator=' / '] - Optional custom separator string to display.
   * @return {Crumb} A separator crumb instance, not clickable and not current.
   */
  static getSeparator(customSeparator = '/') {
    return new Crumb(`\u00A0${customSeparator}\u00A0`, null, false);
  }

  /**
   * Private data storage for Crumb properties.
   */
  #data;

  /**
   * @param {string} title - The display title of the crumb
   * @param {(() => void) | null | undefined} onClick - Optional click handler function
   * @param {boolean} isCurrent - indicates current step
   * @param {string} [dataTest] - Optional data attribute for testing purposes
   * @throws {TypeError} If onClick is not a function, null, or undefined
   */
  constructor(title, onClick, isCurrent, dataTest = null) {
    if (onClick != null && typeof onClick !== 'function') {
      throw new TypeError('Crumb onClick must be null, undefined, or a function');
    }

    this.#data = { title, onClick, isCurrent, key: Crumb.#incrementAndReturnCounter(), dataTest };
  }

  /**
   * Gets the title of the breadcrumb.
   * @returns {string} The title of the breadcrumb.
   */
  get title() {
    return this.#data.title;
  }

  /**
   * Gets the click handler function of the breadcrumb.
   * @returns {(() => void) | null | undefined} The click handler function.
   */
  get onClick() {
    return this.#data.onClick;
  }

  /**
   * Indicates if this breadcrumb is the current step.
   * @returns {boolean} True if this breadcrumb is the current step, otherwise false.
   */
  get isCurrent() {
    return this.#data.isCurrent;
  }

  /**
   * Gets the unique key for this breadcrumb instance.
   * @returns {number} The unique key for this breadcrumb instance.
   */
  get key() {
    return this.#data.key;
  }

  /**
   * Gets the data-test attribute value for testing purposes.
   * @returns {string | null} The data-test attribute value, or null if not set.
   */
  get dataTest() {
    return this.#data.dataTest;
  }
}

/**
 * Renders a breadcrumb navigation component based on an array of Crumb instances.
 * Each crumb can be rendered as a clickable button, bold text for the current step, or greyed out text for non-clickable steps.
 * The component is memoized to optimize performance and prevent unnecessary re-renders when the crumbs data does not change.
 * @param {Object} props - The component props.
 * @param {Crumb[]} props.crumbs - An array of Crumb instances representing the breadcrumb trail.
 * @returns {JSX.Element} The rendered breadcrumbs UI.
 */
function Breadcrumbs({ crumbs }) {
  /**
   * Renders a single breadcrumb based on its properties (current, clickable, or not clickable).
   * @param {Crumb} crumb - The breadcrumb to render.
   * @returns {JSX.Element} The rendered breadcrumb element.
   */
  const renderCrumb = crumb => {
    if (crumb.isCurrent) {
      return renderCurrent(crumb);
    }

    if (typeof crumb.onClick === 'function') {
      return renderClickable(crumb);
    }

    return renderNotClickable(crumb);
  };

  /**
   * Renders the current breadcrumb as bold text.
   * @param {Crumb} crumb - The breadcrumb to render.
   * @returns {JSX.Element} The rendered current breadcrumb.
   */
  const renderCurrent = crumb => {
    return (
      <strong
        key={crumb.key}
        className="breadcrumb-current text-ellipsis"
        title={crumb.title}
        data-test={crumb.dataTest}
      >
        {crumb.title}
      </strong>
    );
  };

  /**
   * Renders a clickable breadcrumb as a button.
   * @param {Crumb} crumb - The breadcrumb to render.
   * @returns {JSX.Element} The rendered clickable breadcrumb.
   */
  const renderClickable = crumb => {
    return (
      <button
        key={crumb.key}
        type="button"
        className="breadcrumb-clickable text-ellipsis"
        onClick={crumb.onClick}
        title={crumb.title}
        data-test={crumb.dataTest}
      >
        {crumb.title}
      </button>
    );
  };

  /**
   * Renders a non-clickable breadcrumb as greyed out text.
   * @param {Crumb} crumb - The breadcrumb to render.
   * @returns {JSX.Element} The rendered non-clickable breadcrumb.
   */
  const renderNotClickable = crumb => {
    return (
      <span
        key={crumb.key}
        className="breadcrumb-not-clickable text-ellipsis"
        title={crumb.title}
        data-test={crumb.dataTest}
      >
        {crumb.title}
      </span>
    );
  };

  return React.useMemo(() => {
    return <div className="wizard-breadcrumbs">{crumbs.map(crumb => renderCrumb(crumb))}</div>;
  }, [crumbs]);
}

const propTypes = {
  crumbs: PropTypes.arrayOf(PropTypes.instanceOf(Crumb)).isRequired,
};

Breadcrumbs.propTypes = propTypes;

export default Breadcrumbs;
